import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import CityIcon from '@common/assets/icons/city.svg?react';
import PencilIcon from '@common/assets/icons/pencil.svg?react';
import { Badge } from '@common/ui/Badge';
import { Button } from '@common/ui/buttons/Button';
import { Card } from '@common/ui/Card';
import { Heading } from '@common/ui/typography/Heading';
import { Paragraph } from '@common/ui/typography/Paragraph';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import * as dicts from '@entities/profile/dicts';

import { type User } from '@services/UsersService';

import styles from './ProfileInfo.module.scss';
import { getZodiacCompatibility } from './utils/getZodiacCompatibility';
import { getZodiacResult } from './utils/getZodiacResult';

export type ProfileInfoProps = {
  profile: User;
};

export const ProfileInfo: React.FC<ProfileInfoProps> = ({ profile }) => {
  const auth = useAuthStore();
  const reportProfileId = profile.id;

  const [isShowZodiacResult, setIsShowZodiacResult] = useState(false);
  const [isLoadingZodiacResult, setIsLoadingZodiacResult] = useState(false);

  const zodiacCompatibility = getZodiacCompatibility({
    partnerSign: profile.zodiac,
    userSign: auth?.user?.zodiac,
  });

  useEffect(() => {
    setIsShowZodiacResult(false);
  }, [profile]);

  const handleShowZodiacResult = () => {
    setIsLoadingZodiacResult(true);

    setTimeout(() => {
      setIsShowZodiacResult(true);
      setIsLoadingZodiacResult(false);
    }, 500);
  };

  const zodiacResult = getZodiacResult(zodiacCompatibility);

  return (
    <div className={styles.profileInfo}>
      <Card testId="profile-info-base" className={styles.base}>
        <div className={styles.name}>
          {profile.username}, {profile.age}
        </div>
        <div className={styles.city}>
          <CityIcon /> {profile.city}
        </div>
        <div className={styles.about}>
          <PencilIcon /> <span>{profile.about}</span>
        </div>
      </Card>
      <Card testId="profile-info-goal">
        {dicts.goalDisplayDict[profile.goal]}
      </Card>
      <Card testId="profile-info-short" className={styles.short}>
        <Heading level={3} style={{ padding: 0 }}>
          В двух словах
        </Heading>
        <div className={styles.listBadges}>
          {[
            dicts.zodiacSignDict[profile.zodiac],
            profile.height ? profile.height + ' см' : undefined,
            profile.views_on_alcohol !== undefined &&
              dicts.alcoholViewDict[profile.views_on_alcohol],
            profile.sports !== undefined && dicts.sportsDict[profile.sports],
            profile.children !== undefined &&
              dicts.childrenStatusDict[profile.children],
          ]
            .filter(Boolean)
            .map((item, index) => (
              <Badge
                key={index}
                testId="profile-info-badge-short"
                color="actionSecondary"
                text={String(item)}
              />
            ))}
        </div>
      </Card>
      <Card testId="profile-info-zodiac" className={styles.short}>
        <Heading level={3} style={{ padding: 0, width: '80%' }}>
          {isShowZodiacResult
            ? `${zodiacCompatibility}% — совместимость по знаку зодиака`
            : 'Ваша совместимость по знаку зодиака'}
        </Heading>
        <div className={styles.zodiacContainer}>
          {isShowZodiacResult ? (
            <>
              <div className={styles.zodiacBarContainer}>
                <div className={styles.zodiacBarColoredTrack} />
                <div
                  className={styles.zodiacBarColoredThumb}
                  style={{
                    left: `${zodiacCompatibility}%`,
                    background: zodiacResult.color,
                  }}
                />
              </div>
              <Paragraph compact variant="primary">
                <span data-test-id="profile-info-zodiac-result">
                  {zodiacResult.text}
                </span>
              </Paragraph>
            </>
          ) : (
            <>
              <div className={styles.zodiacBarContainer}>
                <div className={styles.zodiacBarDefaultTrack} />
                <div className={styles.zodiacBarDefaultThumb}>
                  <span>?</span>
                </div>
              </div>
              <Button
                testId="profile-info-zodiac-calculate-button"
                variant="actionPrimary"
                loading={isLoadingZodiacResult}
                style={{ width: '100%' }}
                onClick={handleShowZodiacResult}
              >
                Рассчитать
              </Button>
            </>
          )}
        </div>
      </Card>

      {(profile.interests?.length ?? 0) > 0 && (
        <Card testId="profile-info-interests" className={styles.interests}>
          <Heading level={3} style={{ padding: 0 }}>
            Интересы
          </Heading>
          <div className={styles.listBadges}>
            {(profile.interests ?? []).map((interest, index) => (
              <Badge
                testId="profile-info-badge-interests"
                key={index}
                color="actionSecondary"
                text={dicts.interestDict[interest]}
              />
            ))}
          </div>
        </Card>
      )}
      {(profile.study_and_work?.length ?? 0) > 0 && (
        <Card
          testId="profile-info-study-and-work"
          className={styles.studyAndWork}
        >
          <Heading level={3} style={{ padding: 0 }}>
            Учёба и работа
          </Heading>
          <div className={styles.listBadges}>
            {(profile.study_and_work ?? []).map((item, index) => (
              <Badge
                testId="profile-info-badge-study-and-work"
                key={index}
                color="actionSecondary"
                text={item}
              />
            ))}
          </div>
        </Card>
      )}
      <Card testId="profile-info-report" className={styles.report}>
        <Heading level={3} style={{ padding: 0 }}>
          Пользователь нарушает правила?
        </Heading>
        <Paragraph compact variant="secondary">
          Сообщи об этом нам
        </Paragraph>
        <Link
          data-test-id="profile-info-report-link"
          to="/dating/report"
          search={{
            profileId: reportProfileId,
            type: 'feed',
          }}
        >
          <Button
            testId="profile-info-report-button"
            variant="negativeSecondary"
            size={400}
            style={{ width: 'fit-content' }}
          >
            Пожаловаться
          </Button>
        </Link>
      </Card>
    </div>
  );
};
